import {
    ClockIcon,
    CurrencyDollarIcon,
    Grid01Icon,
    Hash02Icon,
    Home02Icon,
    MarkerPin01Icon,
    RulerIcon
} from '@components/ui/icon'
import {cn} from '@utilities/helpers'
import {Assignment} from '@features/assignment/types.ts'
import {useTranslation} from '@/translations/i18n.tsx'
import {TASK_CATEGORY_REVIEW} from '@features/assignment/utils.tsx'

const getDistance = ({distance_km, distance_mile}: Pick<Assignment, 'distance_km' | 'distance_mile'>) =>
    [distance_km != null && `${Math.round(distance_km)}km`, distance_mile != null && `${Math.round(distance_mile)}mi`]
        .filter(Boolean)
        .join('/')

const dataItemClasses =
    'flex items-center gap-2 text-sm text-neutral-900 [&_a]:text-neutral-900 [&_a]:cursor-pointer [&_svg]:size-5 [&_svg]:fill-neutral-600'

export const AssignmentData = ({assignment}: {assignment: Assignment}) => {
    const {t} = useTranslation()
    const isReviewCompleted =
        assignment.task_category.name == TASK_CATEGORY_REVIEW && assignment.task_status.status == 'completed'

    const distance = getDistance(assignment)

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-display-sm font-bold">
                {assignment.entity.title ?? t('commons:listing_n_x', {externalId: assignment.entity.external_id})}
            </h2>
            <div className="flex flex-wrap gap-6">
                <div className={cn(dataItemClasses, 'capitalize')}>
                    <Grid01Icon />
                    {`${assignment.task_category.name} ${assignment.task_media.name}`}
                </div>
                <div className={dataItemClasses}>
                    <Hash02Icon />
                    <a href={assignment.airbnb_link} target="_blank">
                        {assignment.entity.external_id}
                    </a>
                </div>
                {!isReviewCompleted && (
                    <div className={dataItemClasses}>
                        <MarkerPin01Icon />
                        {!!assignment.entity.address && (
                            <a
                                href={`https://www.google.com/maps/place/${assignment.entity.address}%20${assignment.entity.city ?? ''}`}
                                target="_blank"
                            >
                                {assignment.entity.address}
                            </a>
                        )}
                    </div>
                )}
                {!isReviewCompleted && distance && (
                    <div className={dataItemClasses}>
                        <RulerIcon />
                        {t('assignment:travel_distance', {distance})}
                    </div>
                )}
                {!!assignment.entity.bedrooms && (
                    <div className={dataItemClasses}>
                        <Home02Icon /> {t('assignment:x_bedrooms', {count: assignment.entity.bedrooms})}
                    </div>
                )}
                {!!assignment.entity.experience_duration && (
                    <div className={dataItemClasses}>
                        <ClockIcon /> {t('assignment:x_hours', {count: assignment.entity.experience_duration})}
                    </div>
                )}
                {assignment.task_category.name != TASK_CATEGORY_REVIEW && (
                    <div className={dataItemClasses}>
                        <CurrencyDollarIcon />
                        {assignment.quote?.amount
                            ? t('assignment:host_pricing_tier_paid')
                            : t('assignment:host_pricing_tier_free')}
                    </div>
                )}
            </div>
        </div>
    )
}
